import { type ChainAvailabilityItem } from '@/models/appointment-chain-availability.ts';
import { type AppointmentChainConfig } from '@/models/appointment-chain-config.ts';
import { Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import LocationOn from '@material-ui/icons/LocationOn';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';

import ChainCounts from './chain-counts.tsx';
import ChainMetaIcon from './chain-meta-icon.tsx';
import ChainSuggestionsTable from './chain-suggestions-table.tsx';
import { countDistinctDays, resolveChainName } from './chain-utils.ts';

const useStyles = makeStyles((theme) => ({
    card: {
        backgroundColor: 'white',
        maxWidth: theme.layout.contentWidth,
        borderRadius: theme.radius.default,
        boxSizing: 'border-box',
        marginBottom: 20,
    },
    borderCard: { border: theme.border.light, padding: 20 },
    noBorderCard: { padding: 0 },
    chainName: {
        ...theme.typography.HL3,
        marginTop: 8,
    },
    practiceRow: {
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        marginTop: 8,
        marginBottom: 16,
        fontSize: '0.875rem',
        color: theme.palette.common.midGrey,
    },
}));

interface Props {
    items: ChainAvailabilityItem[];
    selectedChainConfig: AppointmentChainConfig;
    resolveStepLabel: (appointmentTypeKey: string) => string;
    practiceName: string;
    practiceAddress?: string;
    noBorder?: boolean;
}

const ChainOverviewCard = ({
    items,
    selectedChainConfig,
    resolveStepLabel,
    practiceName,
    practiceAddress,
    noBorder,
}: Props) => {
    const classes = useStyles();
    const { i18n } = useTranslation();

    const chainName = resolveChainName(selectedChainConfig, i18n.language);
    const stepCount = selectedChainConfig.steps.length;
    const appointmentCount = countDistinctDays(items);
    const practiceLine = [practiceName, practiceAddress].filter(Boolean).join(' / ');

    return (
        <div
            className={clsx(classes.card, noBorder ? classes.noBorderCard : classes.borderCard)}
            data-testid="chain-overview-card"
        >
            <Typography variant="h2" className={classes.chainName}>
                {chainName}
            </Typography>
            <ChainCounts stepCount={stepCount} appointmentCount={appointmentCount} />
            <div className={classes.practiceRow}>
                <ChainMetaIcon icon={LocationOn} />
                <Typography variant="inherit" component="span">
                    {practiceLine}
                </Typography>
            </div>
            <ChainSuggestionsTable
                items={items}
                selectedChainConfig={selectedChainConfig}
                resolveStepLabel={resolveStepLabel}
                showBanner={false}
            />
        </div>
    );
};

export default ChainOverviewCard;
